import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { handleAuthError } from '../utils/errorHandler';
import { LanguageToggle } from '../components/ui';
import { ThemeToggle } from '../components/common';

// TechPulse Logo Component for Login
const TechPulseLogo = () => (
  <div className="flex flex-col items-center gap-3">
    <svg
      viewBox="0 0 100 100"
      className="w-16 h-16"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="loginGrad" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#1B6FF2" />
          <stop offset="50%" stopColor="#00C2CB" />
          <stop offset="100%" stopColor="#28C76F" />
        </linearGradient>
      </defs>
      <rect width="100" height="100" rx="22" fill="url(#loginGrad)" />
      <g fill="#FFFFFF">
        <rect x="12" y="16" width="34" height="10" rx="3" />
        <rect x="12" y="26" width="10" height="36" rx="3" />
        <rect x="54" y="16" width="10" height="52" rx="3" />
        <path d="M64 16 C86 16, 86 46, 64 46 L64 36 C74 36, 74 26, 64 26 Z" fillRule="evenodd" />
        <path d="M36 68 L36 52 L28 52 L40 36 L52 52 L44 52 L44 68 Z" />
      </g>
    </svg>
    <h1 className="font-heading text-2xl font-semibold">
      <span className="text-brand-navy dark:text-white">Tech</span>
      <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue via-brand-cyan to-brand-green">
        Pulse
      </span>
    </h1>
  </div>
);

export default function Login() {
  const { t } = useTranslation(['auth', 'common']);
  const [isLogin, setIsLogin] = useState(true);
  const [isReset, setIsReset] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, signup, resetPassword } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e?: FormEvent) {
    if (e) e.preventDefault();

    try {
      setError('');
      setMessage('');
      setLoading(true);

      if (isReset) {
        await resetPassword(email);
        setMessage(t('auth:resetPassword.success'));
        setLoading(false);
      } else if (isLogin) {
        await login(email, password);
        navigate('/');
      } else {
        // Signup flow - validate name first
        if (!name || name.trim() === '') {
          setError(t('auth:register.nameRequired'));
          setLoading(false);
          return;
        }

        // Create auth account
        const res = await signup(email, password);

        // Create Firestore user document - MUST complete before navigation
        try {
          await setDoc(doc(db, 'users', res.user.uid), {
            email: email,
            name: name.trim(),
            role: 'invite',
            createdAt: new Date().toISOString(),
          });

          // Success - show message and let AuthContext reload to show WelcomeScreen
          setMessage(t('auth:register.success'));
          setLoading(false);

          // Small delay to let AuthContext detect the new user before navigation
          setTimeout(() => {
            navigate('/');
          }, 1000);
        } catch (docError) {
          setError(t('auth:register.profileError', { error: (docError as Error).message }));
          setLoading(false);
          // Don't navigate if doc creation failed
          return;
        }
      }
    } catch (err) {
      setError(handleAuthError(err as Error));
      setLoading(false);
    }
  }

  const getTitle = (): string => {
    if (isReset) return t('auth:resetPassword.title');
    if (isLogin) return t('auth:login.title');
    return t('auth:register.title');
  };

  const getButtonText = (): string => {
    if (loading) return t('common:status.loading');
    if (isReset) return t('auth:resetPassword.submit');
    if (isLogin) return t('auth:login.submit');
    return t('auth:register.submit');
  };

  const getToggleText = (): string => {
    if (isReset) return t('auth:resetPassword.backToLogin');
    if (isLogin) return t('auth:login.noAccount');
    return t('auth:register.hasAccount');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-light-card dark:bg-dark-bg transition-colors">
      {/* Language and Theme toggles */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <LanguageToggle />
        <ThemeToggle />
      </div>

      <div className="bg-white dark:bg-dark-card p-8 rounded-xl shadow-card dark:shadow-dark-card w-full max-w-md mx-4 animate-fade-in border border-light-border dark:border-dark-border">
        <div className="text-center mb-8">
          <TechPulseLogo />
          <h2 className="text-xl font-semibold text-light-text dark:text-dark-text mt-4">
            {getTitle()}
          </h2>
        </div>

        {error && (
          <div
            id="form-error"
            role="alert"
            aria-live="polite"
            className="bg-error-light dark:bg-error/20 text-error-dark dark:text-error p-3 rounded-lg mb-4 text-sm animate-fade-in"
          >
            {error}
          </div>
        )}
        {message && (
          <div
            id="form-message"
            role="status"
            aria-live="polite"
            className="bg-success-light dark:bg-success/20 text-success-dark dark:text-success p-3 rounded-lg mb-4 text-sm animate-fade-in"
          >
            {message}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="space-y-4"
          aria-label={getTitle()}
          aria-describedby={error ? 'form-error' : undefined}
        >
          {!isLogin && !isReset && (
            <div>
              <label
                htmlFor="name-input"
                className="block text-sm font-medium text-light-text dark:text-dark-text mb-1.5"
              >
                {t('auth:register.name')}
              </label>
              <input
                id="name-input"
                type="text"
                className="input-base"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t('auth:register.namePlaceholder')}
                required
                autoComplete="name"
              />
            </div>
          )}

          <div>
            <label
              htmlFor="email-input"
              className="block text-sm font-medium text-light-text dark:text-dark-text mb-1.5"
            >
              {t('auth:login.email')}
            </label>
            <input
              id="email-input"
              type="email"
              className="input-base"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t('auth:login.emailPlaceholder')}
              required
              autoComplete="email"
              aria-describedby={error ? 'form-error' : undefined}
            />
          </div>

          {!isReset && (
            <div>
              <label
                htmlFor="password-input"
                className="block text-sm font-medium text-light-text dark:text-dark-text mb-1.5"
              >
                {t('auth:login.password')}
              </label>
              <input
                id="password-input"
                type="password"
                className="input-base"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={t('auth:login.passwordPlaceholder')}
                required
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                aria-describedby={error ? 'form-error' : undefined}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary w-full mt-6"
            aria-busy={loading}
          >
            {getButtonText()}
          </button>
        </form>

        <div className="mt-6 text-center space-y-3">
          {!isReset && (
            <button
              onClick={() => setIsReset(true)}
              className="block w-full text-accent-600 dark:text-accent-400 hover:underline text-sm"
            >
              {t('auth:login.forgotPassword')}
            </button>
          )}

          <button
            onClick={() => {
              setIsReset(false);
              setIsLogin(!isLogin);
              setError('');
              setMessage('');
            }}
            className="block w-full text-accent-600 dark:text-accent-400 hover:underline text-sm"
          >
            {getToggleText()}
          </button>
        </div>
      </div>
    </div>
  );
}
